import {
    useState,
    useMemo
} from 'react'
import { useFormContext } from 'react-hook-form'
import { useMethods } from '@shared/hooks/methods'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import CloseIcon from '@mui/icons-material/Close'
import InfiniteMasonry from '@shared/components/InfiniteMasonry'

const DEFAULT_HEIGHT = 200

function ImageWithSkeleton({
    src,
    onClick
}) {
    const [loaded, setLoaded] = useState(false)

    return (
        <>
            {!loaded && (
                <Skeleton
                    variant='rectangular'
                    animation='wave'
                    width='100%'
                    height={DEFAULT_HEIGHT}
                />
            )}
            <img
                src={src}
                onClick={onClick}
                onLoad={() => setLoaded(true)}
                style={{
                    width: '100%',
                    objectFit: 'cover',
                    display: loaded ? 'block' : 'none',
                }}
            />
        </>
    )
}

export default function Images({
    images,
    onCrop
}) {
    const { setValue, getValues } = useFormContext()
    const { showMessage } = useMethods()
    const [selectedUser, setSelectedUser] = useState('الكل')

    const uniqueUsers = useMemo(() =>
        ['الكل', ...new Set(images.map(img => img.user).filter(Boolean))],
    [images])

    const filteredImages = useMemo(() => 
        selectedUser === 'الكل' ? images : images.filter(img => img.user === selectedUser),
    [images, selectedUser])

    const deleteItem = id => {
        const prev = getValues('images')
        const index = prev.findIndex(i => i.id === id)
        const removedItem = prev[index]

        setValue('images', prev.filter(i => i.id !== id))

        let undone = false
        const timeoutId = setTimeout(() => {
            if (!undone) URL.revokeObjectURL(removedItem.url)
        }, 6000)

        showMessage({
            text: 'تم الحذف',
            action: {
                text: 'تراجع',
                onClick: () => {
                    undone = true
                    clearTimeout(timeoutId)
                    const current = getValues('images')
                    const restored = [...current]
                    restored.splice(index, 0, removedItem)
                    setValue('images', restored)
                    showMessage(null)
                }
            }
        })
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {uniqueUsers.length > 2 && (
                <FormControl
                    sx={{
                        minWidth: 200,
                        alignSelf: 'flex-start'
                    }}
                    size='small'
                >
                    <InputLabel id='user-filter-label'>المستخدم</InputLabel>
                    <Select
                        labelId='user-filter-label'
                        value={selectedUser}
                        label='المستخدم'
                        onChange={(e) => setSelectedUser(e.target.value)}
                    >
                        {uniqueUsers.map(user => (
                            <MenuItem
                                key={user}
                                value={user}
                            >{user}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            <InfiniteMasonry
                items={filteredImages}
                renderItem={(image, isLast, lastRef) => (
                    <Box
                        ref={isLast ? lastRef : null}
                        sx={{
                            position: 'relative',
                            minWidth: '100px',
                            maxHeight: DEFAULT_HEIGHT,
                            overflow: 'hidden',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: 'divider'
                        }}
                    >
                        <IconButton
                            size='small'
                            sx={{
                                top: 5,
                                position: 'absolute',
                                insetInlineEnd: 5,
                                zIndex: 1,
                                padding: .2,
                                backgroundColor: 'rgb(100, 100, 100)',
                                '&:hover': { backgroundColor: 'rgba(100, 100, 100, .7)' }
                            }}
                            onClick={() => deleteItem(image.id)}
                        >
                            <CloseIcon
                                fontSize='small'
                                sx={{
                                    color: 'white',
                                    fontSize: 15
                                }}
                            />
                        </IconButton>

                        <ImageWithSkeleton
                            src={image.url || image.image}
                            onClick={onCrop}
                        />
                    </Box>
                )}
            />
        </Box>
    )
}